/**
 * Offline-Aware API Service
 * Wraps API calls to handle offline scenarios with automatic queueing
 */

import { 
  saveOfflineIncident, 
  addToSyncQueue,
  cacheData,
  getCachedData
} from './offlineStorage';
import { isOnline, requestBackgroundSync } from './syncManager';

const API_BASE = '/api';

/**
 * Make an API request with offline handling
 */
const offlineAwareRequest = async (url, options = {}, offlineConfig = {}) => {
  const { 
    queueIfOffline = false,
    cacheKey = null,
    cacheResponse = false,
    fallbackToCache = true,
    operationType = null 
  } = offlineConfig;

  // If online, try the request
  if (isOnline()) {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = response.status !== 204 ? await response.json() : null;
      
      // Cache successful GET responses if configured
      if (cacheResponse && cacheKey && data) {
        await cacheData(cacheKey, data);
      }

      return { success: true, data, fromCache: false };
    } catch (error) {
      // Network error - might be going offline
      if (!navigator.onLine && fallbackToCache && cacheKey) {
        const cached = await getCachedData(cacheKey);
        if (cached) {
          return { success: true, data: cached, fromCache: true };
        }
      }
      throw error;
    }
  }

  // Offline handling
  if (queueIfOffline && options.method && options.method !== 'GET') {
    // Queue mutation operations for later sync
    const queueItem = await addToSyncQueue({
      type: operationType || options.method,
      method: options.method,
      url,
      body: options.body ? JSON.parse(options.body) : null
    });

    // Request background sync if supported
    requestBackgroundSync().catch(console.error);

    return { 
      success: true, 
      queued: true, 
      queueId: queueItem.id,
      message: 'Request queued for sync when online'
    };
  }

  // For GET requests, try cache
  if (fallbackToCache && cacheKey) {
    const cached = await getCachedData(cacheKey);
    if (cached) {
      return { success: true, data: cached, fromCache: true };
    }
  }

  throw new Error('Offline and no cached data available');
};

/**
 * Create authorization headers
 */
const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token && { 'Authorization': `Bearer ${token}` })
});

// ============ INCIDENTS API ============

/**
 * Create a new incident (with offline support)
 */
export const createIncident = async (incidentData, token) => {
  if (!isOnline()) {
    // Save locally for offline
    const localIncident = await saveOfflineIncident(incidentData);
    requestBackgroundSync().catch(console.error);
    
    return {
      success: true,
      offline: true,
      localId: localIncident.localId,
      incident: localIncident,
      message: 'Incident saved locally. Will sync when online.'
    };
  }

  // Online - send directly
  const response = await fetch(`${API_BASE}/incidents`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(incidentData)
  });

  if (!response.ok) {
    throw new Error(`Failed to create incident: ${response.statusText}`);
  }

  return {
    success: true,
    offline: false,
    incident: await response.json()
  };
};

/**
 * Get incidents list (with caching)
 */
export const getIncidents = async (token, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE}/incidents${queryString ? `?${queryString}` : ''}`;
  const cacheKey = `incidents-${queryString || 'all'}`;

  return offlineAwareRequest(url, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey,
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Get single incident
 */
export const getIncident = async (id, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: `incident-${id}`,
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Update incident
 */
export const updateIncident = async (id, incidentData, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(incidentData)
  }, {
    queueIfOffline: true,
    operationType: 'update-incident'
  });
};

/**
 * Close incident
 */
export const closeIncident = async (id, closeData, token) => {
  return offlineAwareRequest(`${API_BASE}/incidents/${id}/close`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(closeData)
  }, {
    queueIfOffline: true,
    operationType: 'close-incident'
  });
};

// ============ STATUS API ============

/**
 * Update user status
 */
export const updateStatus = async (statusData, token) => {
  return offlineAwareRequest(`${API_BASE}/status`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(statusData)
  }, {
    queueIfOffline: true,
    operationType: 'status-update'
  });
};

/**
 * Send GPS ping
 */
export const sendGpsPing = async (pingData, token) => {
  // GPS pings are less critical - don't queue if offline
  if (!isOnline()) {
    return { success: false, offline: true, message: 'GPS ping skipped - offline' };
  }

  const response = await fetch(`${API_BASE}/status/ping`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(pingData)
  });

  if (!response.ok) {
    throw new Error(`GPS ping failed: ${response.statusText}`);
  }

  return { success: true, data: await response.json() };
};

// ============ USERS API ============

/**
 * Get users list (cached for offline reference)
 */
export const getUsers = async (token) => {
  return offlineAwareRequest(`${API_BASE}/users`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'users-list',
    cacheResponse: true,
    fallbackToCache: true
  });
};

/**
 * Get current user
 */
export const getCurrentUser = async (token) => {
  return offlineAwareRequest(`${API_BASE}/users/me`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'current-user',
    cacheResponse: true,
    fallbackToCache: true
  });
};

// ============ MESSAGES API ============

/**
 * Send message (with offline queuing)
 */
export const sendMessage = async (messageData, token) => {
  return offlineAwareRequest(`${API_BASE}/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(messageData)
  }, {
    queueIfOffline: true,
    operationType: 'send-message'
  });
};

/**
 * Get messages (cached)
 */
export const getMessages = async (token) => {
  return offlineAwareRequest(`${API_BASE}/messages`, {
    method: 'GET',
    headers: authHeaders(token)
  }, {
    cacheKey: 'messages',
    cacheResponse: true,
    fallbackToCache: true
  });
};

// ============ UTILITY EXPORTS ============

export { isOnline };

export default {
  createIncident,
  getIncidents,
  getIncident,
  updateIncident,
  closeIncident,
  updateStatus,
  sendGpsPing,
  getUsers,
  getCurrentUser,
  sendMessage,
  getMessages,
  isOnline
};
